import React, { useState, useEffect, useCallback } from 'react';
import {
  IconAdd,
  IconArrow,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClose,
  IconDelete,
  IconEdit,
  IconError,
  IconFailed,
  IconInstant,
  IconList,
  IconMessage,
  IconPatients,
  IconPause,
  IconPending,
  IconPlay,
  IconRefresh,
  IconSearch,
  IconSpinner,
  IconSuccess,
  IconUndo,
  IconVoiceCall,
  IconVoiceSMS,
} from '../components/icons';
import { useAuth } from '../context/AuthContext';
import { scheduleAPI } from '../services/api';
import api from '../config/api';
import { cn } from '../utils/cn';
import CampaignCalendar from './CampaignCalendar';
import { approvalNotice, requiresApproval } from '../utils/approval';

// ── Constants ─────────────────────────────────────────────────────────────────

const CHANNEL_OPTIONS = [
  { value: 'smart_sms',      label: 'Smart SMS',   icon: IconMessage, desc: 'AI-generated SMS — delivered directly' },
  { value: 'sms_direct',     label: 'SMS Direct',  icon: IconMessage, desc: 'Pre-written SMS — delivered directly' },
  { value: 'bulk_voice',     label: 'Voice SMS',   icon: IconVoiceSMS,         desc: 'AI voice script — delivered via voice channel' },
  { value: 'elevenlabs_ivr', label: 'AI Voice',    icon: IconVoiceCall,         desc: 'Outbound AI voice calls — fires automatically' },
];

const CADENCE_OPTIONS = [
  { value: 'daily',    label: 'Daily' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly',  label: 'Monthly' },
  { value: 'custom',   label: 'Custom dates' },
  // Not a calendar: this one fires for each patient a set number of days
  // after that patient's interaction with an earlier campaign.
  { value: 'follow_up', label: 'Follow up on an earlier campaign' },
];

const FOLLOW_UP_AUDIENCES = [
  { value: 'all',           label: 'Everyone the earlier campaign reached out to' },
  { value: 'delivered',     label: 'Only those whose message was delivered' },
  { value: 'not_delivered', label: 'Only those it failed to reach' },
  { value: 'no_response',   label: 'Those reached who never replied or answered' },
];

const WEEKDAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tw', label: 'Twi' },
  { value: 'ga', label: 'Ga' },
];

// ── Status helpers ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active:    { label: 'Active',    color: 'bg-primary-50 text-primary-700 border-primary-200' },
  paused:    { label: 'Paused',    color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  completed: { label: 'Completed', color: 'bg-gray-50 text-gray-600 border-gray-200' },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-red-700 border-red-200' },
  draft:     { label: 'Draft',     color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const RUN_STATUS_CONFIG = {
  pending:        { label: 'Pending',         icon: IconPending,       color: 'text-gray-500' },
  generating:     { label: 'Generating',      icon: IconSpinner,     color: 'text-blue-500 animate-spin' },
  pending_review: { label: 'Awaiting Review', icon: IconError, color: 'text-yellow-500' },
  running:        { label: 'Running',         icon: IconSpinner,     color: 'text-primary-500 animate-spin' },
  completed:      { label: 'Completed',       icon: IconSuccess, color: 'text-green-500' },
  failed:         { label: 'Failed',          icon: IconFailed,     color: 'text-red-500' },
  skipped:        { label: 'Skipped',         icon: IconUndo,   color: 'text-gray-400' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border', cfg.color)}>
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onNew }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4">
        <IconCalendar className="h-8 w-8 text-primary-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No scheduled programs yet</h3>
      <p className="text-gray-500 text-sm max-w-sm mb-6">
        Set up a recurring campaign program — define who to reach, what to say, and when to say it.
        The system handles the rest.
      </p>
      <button onClick={onNew} className="btn btn-primary flex items-center space-x-2">
        <IconAdd className="h-4 w-4" />
        <span>Create your first program</span>
      </button>
    </div>
  );
}

// ── Run history panel ─────────────────────────────────────────────────────────

function RunHistory({ programId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    scheduleAPI.getRuns(programId)
      .then(d => setRuns(d.runs || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [programId]);

  if (loading) {
    return <div className="py-4 flex justify-center"><IconSpinner className="h-4 w-4 animate-spin text-gray-400" /></div>;
  }

  if (!runs.length) {
    return <p className="text-sm text-gray-400 py-4 text-center">No runs yet.</p>;
  }

  return (
    <div className="mt-3 space-y-2">
      {runs.map(run => {
        const cfg = RUN_STATUS_CONFIG[run.status] || RUN_STATUS_CONFIG.pending;
        const Icon = cfg.icon;
        return (
          <div key={run.id} className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon className={cn('h-4 w-4 flex-shrink-0', cfg.color)} />
              <span className="text-gray-700">{cfg.label}</span>
              </div>
            <div className="text-gray-400 text-xs">{formatDate(run.scheduled_at)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Program list column header ────────────────────────────────────────────────

/**
 * The column headings, once, above the list.
 *
 * Each row used to carry its own NEXT RUN / RUNS / CHANNEL captions, which
 * repeated the same three words down the page and made every card two lines
 * taller than it needed to be. The columns are fixed-width and the widths are
 * mirrored here, so the headings sit over the values they name.
 */
function ProgramListHeader() {
  const cell = 'text-[11px] font-medium uppercase tracking-wide text-gray-400';
  return (
    // The transparent border reproduces the 1px the cards below spend on
    // theirs, so the headings sit exactly over their columns instead of a
    // pixel to the left of them.
    <div className="hidden items-center gap-4 border border-transparent px-5 pb-2 lg:flex">
      <div className="w-10 flex-shrink-0" aria-hidden="true" />
      <p className={`min-w-0 flex-1 ${cell}`}>Programme</p>
      <p className={`w-44 flex-shrink-0 ${cell}`}>Next run</p>
      <p className={`w-24 flex-shrink-0 ${cell}`}>Runs</p>
      <p className={`hidden w-36 flex-shrink-0 xl:block ${cell}`}>Channel</p>
      <div className="w-36 flex-shrink-0" aria-hidden="true" />
    </div>
  );
}

// ── Program card ──────────────────────────────────────────────────────────────

function ProgramCard({ program, onPause, onResume, onDelete, onRunNow }) {
  const [expanded, setExpanded] = useState(false);
  const isMulti = program.campaign_type === 'multi';
  const channelTypes = isMulti
    ? (program.campaign_config?.channels || []).map(c => c.type)
    : [program.campaign_type];
  const channelOpts = channelTypes.map(t => CHANNEL_OPTIONS.find(c => c.value === t)).filter(Boolean);
  const PrimaryOpt = channelOpts[0];
  const Icon = PrimaryOpt?.icon || IconCalendar;
  const channelLabel = isMulti
    ? channelOpts.map(o => o?.label || '').filter(Boolean).join(' + ')
    : (PrimaryOpt?.label || program.campaign_type?.replace(/_/g, ' '));

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors hover:border-gray-300">
      {/* One row per programme. The three facts every programme has — when it
          runs next, how often it has run, which channel it uses — sit in
          fixed-width columns rather than a wrapping inline run, so they line up
          with the rows above and below and the eye can scan a column instead of
          re-reading each line. Below lg there isn't room for columns, so they
          collapse back into a single wrapped line under the name. */}
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center text-gray-500">
          <Icon className="h-[18px] w-[18px]" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="truncate text-sm font-semibold text-gray-900">{program.name}</h3>
            <StatusBadge status={program.status} />
          </div>
          {program.description && (
            <p className="mt-0.5 truncate text-xs text-gray-500">{program.description}</p>
          )}
          {/* Narrow screens only — the columns to the right are hidden there. */}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 lg:hidden">
            <span className="flex items-center gap-1">
              <IconPending className="h-3.5 w-3.5" />
              <span className="text-gray-700">{formatDate(program.next_run_at)}</span>
            </span>
            <span>{program.run_count} run{program.run_count !== 1 ? 's' : ''}</span>
            <span className="capitalize">{channelLabel}</span>
          </div>
        </div>

        <p className="hidden w-44 flex-shrink-0 truncate text-[13px] font-medium text-gray-900 lg:block">
          {formatDate(program.next_run_at)}
        </p>

        <p className="hidden w-24 flex-shrink-0 text-[13px] font-medium tabular-nums text-gray-900 lg:block">
          {program.run_count}
          {program.max_runs ? <span className="text-gray-400"> / {program.max_runs}</span> : null}
        </p>

        <div className="hidden w-36 flex-shrink-0 xl:block">
          <span className="inline-flex max-w-full items-center truncate rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium capitalize text-gray-600 ring-1 ring-inset ring-gray-200">
            {channelLabel}
          </span>
        </div>

        {/* Fixed width, matched by the spacer in ProgramListHeader, so the
            columns to the left land in the same place on every row. */}
        <div className="flex w-36 flex-shrink-0 items-center justify-end gap-0.5">
          <button
            onClick={() => onRunNow(program.id)}
            title="Run now (test)"
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600"
          >
            <IconInstant className="h-4 w-4" />
          </button>
          {program.status === 'active' ? (
            <button
              onClick={() => onPause(program.id)}
              title="Pause"
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-yellow-50 hover:text-yellow-700"
            >
              <IconPause className="h-4 w-4" />
            </button>
          ) : program.status === 'paused' ? (
            <button
              onClick={() => onResume(program.id)}
              title="Resume"
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-700"
            >
              <IconPlay className="h-4 w-4" />
            </button>
          ) : null}
          <button
            onClick={() => onDelete(program.id)}
            title="Cancel program"
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <IconDelete className="h-4 w-4" />
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide run history' : 'Show run history'}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100"
          >
            {expanded ? <IconChevronUp className="h-4 w-4" /> : <IconChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Expanded run history */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 pb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-1">Run history</p>
          <RunHistory programId={program.id} />
        </div>
      )}
    </div>
  );
}

// ── 3-step creation form ──────────────────────────────────────────────────────

const DEFAULT_CHANNEL_CONFIGS = {
  smart_sms:      { health_topic: '', campaign_goals: '', language: 'en', max_messages: 3, message_delay_minutes: 60, sender_id: 'CareArena' },
  sms_direct:     { message: '', sender_id: 'CareArena', health_condition: '' },
  bulk_voice:     { disease_name: '', campaign_goals: '', language: 'en' },
  elevenlabs_ivr: { health_topic: '', delay_between_calls: 2 },
};

const INITIAL_FORM = {
  // Step 1
  name: '',
  description: '',
  campaign_types: ['smart_sms'],        // array — supports multi-channel
  // Step 2 – per-channel config (keyed by channel type)
  selected_patients: [],                // [{ id, name, phone }] — shared across all channels
  channel_configs: { ...DEFAULT_CHANNEL_CONFIGS },
  // Step 3 – schedule
  start_date: '',
  end_date: '',
  cadence_type: 'weekly',
  cadence_time: '09:00',
  cadence_days: [1],
  cadence_day: 15,
  cadence_dates: '',
  // Step 3 – follow-up journeys
  follow_up_parent: '',
  follow_up_after_days: 3,
  follow_up_audience: 'all',
  available_programs: [],
  max_runs: '',
};

function StepIndicator({ current }) {
  const steps = ['Details', 'Content', 'Schedule'];
  return (
    <div className="flex items-center space-x-2 mb-8">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = current > n;
        const active = current === n;
        return (
          <React.Fragment key={n}>
            <div className="flex items-center space-x-2">
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2',
                done   ? 'bg-primary-600 border-primary-600 text-white' :
                active ? 'border-primary-600 text-primary-600 bg-white' :
                         'border-gray-200 text-gray-400 bg-white',
              )}>
                {done ? <IconSuccess className="h-4 w-4" /> : n}
              </div>
              <span className={cn('text-sm', active ? 'font-semibold text-gray-900' : 'text-gray-400')}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Step1({ form, onChange }) {
  const toggleChannel = (value) => {
    const current = form.campaign_types || [];
    if (current.includes(value)) {
      if (current.length === 1) return; // must keep at least one
      onChange('campaign_types', current.filter(t => t !== value));
    } else {
      onChange('campaign_types', [...current, value]);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Program name *</label>
        <input
          className="input w-full"
          placeholder="e.g. Preeclampsia 3-month education"
          value={form.name}
          onChange={e => onChange('name', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          className="input w-full resize-none"
          rows={2}
          placeholder="Optional notes for your team"
          value={form.description}
          onChange={e => onChange('description', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Channel types *
          <span className="ml-2 text-xs font-normal text-gray-400">Select one or more</span>
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CHANNEL_OPTIONS.map(opt => {
            const Icon = opt.icon;
            const selected = (form.campaign_types || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleChannel(opt.value)}
                className={cn(
                  'flex items-start space-x-3 p-4 rounded-xl border-2 text-left transition-all',
                  selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                )}
              >
                <div className={cn(
                  'mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                  selected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 bg-white',
                )}>
                  {selected && <IconCheck className="h-3 w-3 text-white" weight="bold" />}
                </div>
                <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', selected ? 'text-primary-600' : 'text-gray-400')} />
                <div>
                  <p className={cn('text-sm font-semibold', selected ? 'text-primary-700' : 'text-gray-900')}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
        {(form.campaign_types || []).length > 1 && (
          <p className="text-xs text-primary-700 mt-2 font-medium bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">
            {form.campaign_types.length} channels selected — you'll configure content for each on the next step.
          </p>
        )}
      </div>
    </div>
  );
}

// ── PatientSelector ───────────────────────────────────────────────────────────

function PatientSelector({ selected, onChange }) {
  const { user } = useAuth();
  const [patients, setPatients]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterDx, setFilterDx]   = useState('All');

  // Fetch patients from REST API
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get('/patients', { params: { user_id: user.id } })
      .then(({ data }) => {
        setPatients(data?.patients ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  const diagnoses = ['All', ...Array.from(new Set(patients.map(p => p.diagnosis).filter(Boolean)))];

  const filtered = patients.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.phone?.includes(q);
    const matchDx = filterDx === 'All' || p.diagnosis === filterDx;
    return matchSearch && matchDx;
  });

  const selectedIds = new Set(selected.map(p => p.id));

  const toggle = (patient) => {
    if (selectedIds.has(patient.id)) {
      onChange(selected.filter(p => p.id !== patient.id));
    } else {
      onChange([...selected, { id: patient.id, name: patient.name, phone: patient.phone }]);
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selectedIds.has(p.id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      // Deselect all filtered
      const filteredIds = new Set(filtered.map(p => p.id));
      onChange(selected.filter(p => !filteredIds.has(p.id)));
    } else {
      // Select all filtered (merge, avoid duplicates)
      const newOnes = filtered.filter(p => !selectedIds.has(p.id))
        .map(p => ({ id: p.id, name: p.name, phone: p.phone }));
      onChange([...selected, ...newOnes]);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between rounded-t-xl">
        <div className="flex items-center space-x-2">
          <IconPatients className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-medium text-gray-700">Select from Patient Database</span>
        </div>
        <span className={cn(
          'text-xs font-semibold px-2 py-0.5 rounded-full',
          selected.length > 0 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
        )}>
          {selected.length} selected
        </span>
      </div>

      {/* IconSearch + filter bar */}
      <div className="px-4 py-3 flex gap-3 border-b border-gray-100">
        <div className="relative flex-1">
          <IconSearch className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
          <input
            className="input w-full pl-8 py-1.5 text-sm"
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
              <IconClose className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {diagnoses.length > 1 && (
          <select
            className="input text-sm py-1.5"
            value={filterDx}
            onChange={e => setFilterDx(e.target.value)}
          >
            {diagnoses.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </div>

      {/* Patient list */}
      <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <IconSpinner className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Loading patients…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">
            {patients.length === 0 ? 'No patients in database yet.' : 'No patients match your search.'}
          </div>
        ) : (
          <>
            {/* Select-all row */}
            <div
              className="flex items-center px-4 py-2.5 bg-gray-50 hover:bg-gray-100 cursor-pointer"
              onClick={toggleAll}
            >
              <input
                type="checkbox"
                readOnly
                checked={allFilteredSelected}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 mr-3"
              />
              <span className="text-xs font-semibold text-gray-600">
                {allFilteredSelected ? 'Deselect all' : `Select all ${filtered.length} shown`}
              </span>
            </div>
            {filtered.map(p => (
              <div
                key={p.id}
                className={cn(
                  'flex items-center px-4 py-2.5 cursor-pointer transition-colors',
                  selectedIds.has(p.id) ? 'bg-primary-50' : 'hover:bg-gray-50',
                )}
                onClick={() => toggle(p)}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={selectedIds.has(p.id)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 mr-3 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.phone}{p.diagnosis ? ` · ${p.diagnosis}` : ''}</p>
                </div>
                {p.status && (
                  <span className={cn(
                    'ml-2 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0',
                    String(p.status).toLowerCase() === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  )}>
                    {p.status}
                  </span>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ChannelConfigFields({ type, cfg, onCfgChange }) {
  if (type === 'sms_direct') return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Message * <span className="text-xs text-gray-400">(max 160 chars)</span>
        </label>
        <textarea className="input w-full resize-none" rows={3} maxLength={160}
          placeholder="Your health check is due. Please monitor your blood pressure."
          value={cfg.message || ''}
          onChange={e => onCfgChange('message', e.target.value)} />
        <p className="text-xs text-gray-400 mt-1">{(cfg.message || '').length}/160</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sender ID</label>
          <input className="input w-full" value={cfg.sender_id || 'CareArena'}
            onChange={e => onCfgChange('sender_id', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Health condition</label>
          <input className="input w-full" placeholder="Preeclampsia" value={cfg.health_condition || ''}
            onChange={e => onCfgChange('health_condition', e.target.value)} />
        </div>
      </div>
    </>
  );

  if (type === 'smart_sms') return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Health topic *</label>
        <input className="input w-full" placeholder="Preeclampsia warning signs" value={cfg.health_topic || ''}
          onChange={e => onCfgChange('health_topic', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Campaign goals</label>
        <input className="input w-full" placeholder="Educate mothers about danger signs and when to seek care"
          value={cfg.campaign_goals || ''} onChange={e => onCfgChange('campaign_goals', e.target.value)} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <select className="input w-full" value={cfg.language || 'en'}
            onChange={e => onCfgChange('language', e.target.value)}>
            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max messages</label>
          <input type="number" className="input w-full" min={1} max={5} value={cfg.max_messages || 3}
            onChange={e => onCfgChange('max_messages', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Delay (min)</label>
          <input type="number" className="input w-full" min={0} value={cfg.message_delay_minutes || 60}
            onChange={e => onCfgChange('message_delay_minutes', e.target.value)} />
        </div>
      </div>
    </>
  );

  if (type === 'bulk_voice') return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Disease / condition *</label>
        <input className="input w-full" placeholder="Preeclampsia" value={cfg.disease_name || ''}
          onChange={e => onCfgChange('disease_name', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Campaign goals</label>
        <input className="input w-full" placeholder="Monthly reminder about blood pressure monitoring"
          value={cfg.campaign_goals || ''} onChange={e => onCfgChange('campaign_goals', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
        <select className="input w-full" value={cfg.language || 'en'}
          onChange={e => onCfgChange('language', e.target.value)}>
          {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>
    </>
  );

  if (type === 'elevenlabs_ivr') return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Health topic *</label>
        <input className="input w-full" placeholder="Preeclampsia education" value={cfg.health_topic || ''}
          onChange={e => onCfgChange('health_topic', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Delay between calls (seconds)</label>
        <input type="number" className="input w-full" min={1} value={cfg.delay_between_calls || 2}
          onChange={e => onCfgChange('delay_between_calls', e.target.value)} />
      </div>
    </>
  );

  return null;
}

function Step2({ form, onChange }) {
  const types = form.campaign_types || [];
  const isMulti = types.length > 1;
  const [expandedChannel, setExpandedChannel] = useState(types[0]);

  const onCfgChange = (type, key, value) => {
    onChange('channel_configs', {
      ...form.channel_configs,
      [type]: { ...(form.channel_configs[type] || {}), [key]: value },
    });
  };

  return (
    <div className="space-y-5">
      {/* Recipients — shared across all channels */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Recipients *</label>
        <PatientSelector
          selected={form.selected_patients}
          onChange={val => onChange('selected_patients', val)}
        />
        {form.selected_patients.length === 0 && (
          <p className="text-xs text-red-400 mt-1">Select at least one patient to continue.</p>
        )}
      </div>

      {/* Per-channel config sections */}
      {isMulti && (
        <p className="text-xs text-gray-500 font-medium">Configure content for each selected channel:</p>
      )}
      {types.map(type => {
        const opt = CHANNEL_OPTIONS.find(o => o.value === type);
        const Icon = opt?.icon || IconCalendar;
        const cfg = form.channel_configs[type] || {};
        const isOpen = !isMulti || expandedChannel === type;

        return (
          // No overflow-hidden: a focus ring is drawn outside the element's
          // box, so clipping this wrapper sliced the top off the ring of every
          // field inside it. The header rounds its own corners instead, which
          // is all the clipping was actually doing.
          <div key={type} className={cn('rounded-xl', isMulti ? 'border border-gray-200' : '')}>
            {isMulti && (
              <button
                type="button"
                onClick={() => setExpandedChannel(isOpen ? null : type)}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-xl',
                  !isOpen && 'rounded-b-xl'
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary-600" />
                  <span className="text-sm font-semibold text-gray-800">{opt?.label} Configuration</span>
                </div>
                {isOpen ? <IconChevronUp className="h-4 w-4 text-gray-400" /> : <IconChevronDown className="h-4 w-4 text-gray-400" />}
              </button>
            )}
            {isOpen && (
              <div className={cn('space-y-4', isMulti && 'px-4 py-4')}>
                <ChannelConfigFields
                  type={type}
                  cfg={cfg}
                  onCfgChange={(key, value) => onCfgChange(type, key, value)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step3({ form, onChange }) {
  const toggleDay = (day) => {
    const current = form.cadence_days || [];
    if (current.includes(day)) {
      onChange('cadence_days', current.filter(d => d !== day));
    } else {
      onChange('cadence_days', [...current, day].sort((a, b) => a - b));
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start date *</label>
          <input type="datetime-local" className="input w-full" value={form.start_date} onChange={e => onChange('start_date', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End date <span className="text-gray-400">(optional)</span></label>
          <input type="datetime-local" className="input w-full" value={form.end_date} onChange={e => onChange('end_date', e.target.value)} />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cadence *</label>
        <select className="input w-full" value={form.cadence_type} onChange={e => onChange('cadence_type', e.target.value)}>
          {CADENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Follow-up — which campaign, how long after, and who */}
      {form.cadence_type === 'follow_up' && (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm text-gray-600">
            This campaign does not run on a calendar. It checks every day and
            messages each patient once, the chosen number of days after their
            own interaction with the earlier campaign.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Follow up on *
            </label>
            <select
              className="input w-full"
              value={form.follow_up_parent || ''}
              onChange={e => onChange('follow_up_parent', e.target.value)}
            >
              <option value="">Choose a campaign…</option>
              {(form.available_programs || []).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Days after</label>
              <input
                type="number" min="1" max="365"
                className="input w-24"
                value={form.follow_up_after_days ?? 3}
                onChange={e => onChange('follow_up_after_days', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time (Africa/Accra, GMT+0)
              </label>
              <input
                type="time" className="input w-32"
                value={form.cadence_time}
                onChange={e => onChange('cadence_time', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
            <select
              className="input w-full"
              value={form.follow_up_audience || 'all'}
              onChange={e => onChange('follow_up_audience', e.target.value)}
            >
              {FOLLOW_UP_AUDIENCES.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Time — always shown */}
      {form.cadence_type !== 'custom' && form.cadence_type !== 'follow_up' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time (Africa/Accra, GMT+0)</label>
          <input type="time" className="input w-32" value={form.cadence_time} onChange={e => onChange('cadence_time', e.target.value)} />
        </div>
      )}

      {/* Weekly / Biweekly — day picker */}
      {(form.cadence_type === 'weekly' || form.cadence_type === 'biweekly') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Days of week *</label>
          <div className="flex space-x-2 flex-wrap gap-y-2">
            {WEEKDAYS.map(d => (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                className={cn(
                  'w-11 h-11 rounded-lg text-sm font-medium border-2 transition-colors',
                  (form.cadence_days || []).includes(d.value)
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Monthly — day of month */}
      {form.cadence_type === 'monthly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Day of month (1–31)</label>
          <input type="number" className="input w-24" min={1} max={31} value={form.cadence_day} onChange={e => onChange('cadence_day', parseInt(e.target.value, 10))} />
        </div>
      )}

      {/* Custom dates */}
      {form.cadence_type === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dates *  <span className="text-xs text-gray-400">(YYYY-MM-DD, one per line)</span>
          </label>
          <textarea
            className="input w-full font-mono text-sm resize-none"
            rows={4}
            placeholder={"2025-02-14\n2025-03-14\n2025-04-14"}
            value={form.cadence_dates}
            onChange={e => onChange('cadence_dates', e.target.value)}
          />
          {form.cadence_time && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Time (Africa/Accra, GMT+0)</label>
              <input type="time" className="input w-32" value={form.cadence_time} onChange={e => onChange('cadence_time', e.target.value)} />
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Max runs <span className="text-gray-400">(optional — leave blank to run until end date)</span>
        </label>
        <input
          type="number"
          className="input w-32"
          min={1}
          placeholder="e.g. 12"
          value={form.max_runs}
          onChange={e => onChange('max_runs', e.target.value)}
        />
      </div>
    </div>
  );
}

function buildSingleChannelConfig(type, cfg, recipients) {
  const base = { recipients };
  switch (type) {
    case 'sms_direct':
      return { ...base, message: cfg.message || '', sender_id: cfg.sender_id || 'CareArena', health_condition: cfg.health_condition || '' };
    case 'smart_sms':
      return { ...base, health_topic: cfg.health_topic || '', campaign_goals: cfg.campaign_goals || '', language: cfg.language || 'en', max_messages_per_recipient: Number(cfg.max_messages || 3), message_delay_minutes: Number(cfg.message_delay_minutes || 60) };
    case 'bulk_voice':
      return { ...base, disease_name: cfg.disease_name || '', campaign_goals: cfg.campaign_goals || '', language: cfg.language || 'en' };
    case 'elevenlabs_ivr':
      return { ...base, health_topic: cfg.health_topic || '', delay_between_calls: Number(cfg.delay_between_calls || 2) };
    default:
      return base;
  }
}

function buildCampaignConfig(form) {
  const recipients = form.selected_patients.map(p => p.phone);
  const types = form.campaign_types || [];

  if (types.length === 1) {
    // Single channel — backward-compatible flat config
    return buildSingleChannelConfig(types[0], form.channel_configs[types[0]] || {}, recipients);
  }

  // Multi-channel — channels array
  return {
    channels: types.map(type => ({
      type,
      ...buildSingleChannelConfig(type, form.channel_configs[type] || {}, recipients),
    })),
  };
}

function buildCadence(form) {
  const cadence = { type: form.cadence_type, time: form.cadence_time };
  if (form.cadence_type === 'weekly' || form.cadence_type === 'biweekly') cadence.days = form.cadence_days;
  if (form.cadence_type === 'monthly') cadence.day = form.cadence_day;
  if (form.cadence_type === 'custom') {
    cadence.dates = form.cadence_dates.split('\n').map(s => s.trim()).filter(Boolean);
    cadence.time = form.cadence_time;
  }
  if (form.cadence_type === 'follow_up') {
    cadence.parent_program_id = form.follow_up_parent || null;
    cadence.after_days = Number(form.follow_up_after_days) || 3;
    cadence.audience = form.follow_up_audience || 'all';
  }
  return cadence;
}

function validateStep(step, form) {
  if (step === 1) {
    if (!form.name.trim()) return 'Program name is required';
    if (!form.campaign_types || form.campaign_types.length === 0) return 'Select at least one channel type';
  }
  if (step === 2) {
    // A follow-up works out its own recipients from the earlier campaign, so
    // asking for a patient list here would be asking for a list it ignores.
    if (form.cadence_type !== 'follow_up' && form.selected_patients.length === 0) {
      return 'Select at least one patient from the database';
    }
    for (const type of (form.campaign_types || [])) {
      const cfg = form.channel_configs[type] || {};
      if (type === 'sms_direct'     && !cfg.message?.trim())      return 'SMS Direct: Message is required';
      if (type === 'smart_sms'      && !cfg.health_topic?.trim()) return 'Smart SMS: Health topic is required';
      if (type === 'bulk_voice'     && !cfg.disease_name?.trim()) return 'Voice SMS: Disease / condition is required';
      if (type === 'elevenlabs_ivr' && !cfg.health_topic?.trim()) return 'AI Voice: Health topic is required';
    }
  }
  if (step === 3) {
    if (form.cadence_type === 'follow_up' && !form.follow_up_parent) {
      return 'Choose which campaign this one follows up on';
    }
    if (!form.start_date) return 'Start date is required';
    if (form.cadence_type !== 'custom' && !form.cadence_time) return 'Time is required';
    if ((form.cadence_type === 'weekly' || form.cadence_type === 'biweekly') && !form.cadence_days.length) return 'Select at least one day';
    if (form.cadence_type === 'custom' && !form.cadence_dates.trim()) return 'Enter at least one date';
  }
  return null;
}

function CreateModal({ onClose, onCreated, userId, programs = [] }) {
  // Campaigns this one could follow up on. A campaign cannot follow
  // itself, and one that is gone is not a thing to follow.
  const followable = programs.filter(p => p.status !== 'cancelled');
  const [step, setStep]   = useState(1);
  const [form, setForm]   = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const onChange = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const next = () => {
    const err = validateStep(step, form);
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const back = () => { setError(''); setStep(s => s - 1); };

  const submit = async () => {
    const err = validateStep(3, form);
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    try {
      const types = form.campaign_types || [];
      const isSingle = types.length === 1;
      await scheduleAPI.create({
        name: form.name,
        description: form.description,
        campaign_type: isSingle ? types[0] : 'multi',
        start_date: new Date(form.start_date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : undefined,
        cadence: buildCadence(form),
        max_runs: form.max_runs ? Number(form.max_runs) : undefined,
        campaign_config: buildCampaignConfig(form),
        user_id: userId,
      });
      // Start date so the calendar can jump to that month; campaign type so
      // the confirmation can say whether an approval is still owed.
      onCreated(
        form.start_date ? new Date(form.start_date) : new Date(),
        isSingle ? types[0] : 'multi',
      );
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create program');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">New campaign program</h2>
          <p className="text-sm text-gray-500 mt-0.5">Set up a recurring scheduled campaign</p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <StepIndicator current={step} />
          {step === 1 && <Step1 form={form} onChange={onChange} />}
          {step === 2 && <Step2 form={form} onChange={onChange} />}
          {step === 3 && <Step3 form={{ ...form, available_programs: followable }} onChange={onChange} />}
          {/* Say what happens after "Create program". Smart SMS parks every
              run in the Review Queue, and nothing said so -- people saw the
              success toast and believed the campaign was live. */}
          {step === 3 && approvalNotice(
            (form.campaign_types || []).length === 1 ? form.campaign_types[0] : 'multi',
          ) && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">
                {approvalNotice((form.campaign_types || []).length === 1 ? form.campaign_types[0] : 'multi').title}
              </p>
              <p className="mt-1 text-sm text-amber-800">
                {approvalNotice((form.campaign_types || []).length === 1 ? form.campaign_types[0] : 'multi').body}
              </p>
              {/* Say where that happens. The helper has carried this link
                  since the notice was added and nothing rendered it, so the
                  warning named a place people still had to go and find. */}
              <p className="mt-2 text-sm text-amber-800">
                You will see it under{' '}
                <span className="font-semibold">Review Queue</span>, and the
                sidebar shows a count when something is waiting.
              </p>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              <IconError className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 pt-4 border-t border-gray-100 flex items-center justify-between">
          <button onClick={step === 1 ? onClose : back} className="btn btn-secondary">
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 3 ? (
            <button onClick={next} className="btn btn-primary inline-flex items-center gap-1.5">Next <IconArrow className="h-4 w-4" /></button>
          ) : (
            <button onClick={submit} disabled={saving} className="btn btn-primary flex items-center space-x-2 disabled:opacity-60">
              {saving && <IconSpinner className="h-4 w-4 animate-spin" />}
              <span>{saving ? 'Saving…' : 'Create program'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── mNotify scheduled queue panel ────────────────────────────────────────────

function MNotifyQueuePanel() {
  const [queue, setQueue]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await scheduleAPI.getMNotifyQueue();
      setQueue(data.messages || data || []);
    } catch (e) {
      setError(e?.userMessage || 'Failed to load SMS queue');
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const startEdit = (msg) => {
    setSaveError('');
    setEditingId(msg.id);
    setEditForm({
      sender: msg.sender || '',
      message: msg.message || '',
      scheduled_date: msg.scheduled_date || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); setSaveError(''); };

  const saveEdit = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await scheduleAPI.updateMNotifyMessage(editingId, editForm);
      setEditingId(null);
      loadQueue();
    } catch (e) {
      setSaveError(e?.userMessage || 'Failed to update message');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          SMS Scheduled Queue
        </h2>
        <button
          onClick={loadQueue}
          className="p-1.5 rounded text-gray-400 hover:bg-gray-100 transition-colors"
          title="Refresh queue"
        >
          <IconRefresh className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
        </button>
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
          <IconError className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <IconSpinner className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : queue.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-400 bg-gray-50 rounded-xl border border-gray-100">
          No scheduled messages in the SMS queue.
        </div>
      ) : (
        <div className="space-y-2">
          {queue.map(msg => (
            <div key={msg.id} className="bg-white border border-gray-200 rounded-xl p-4">
              {editingId === msg.id ? (
                <div className="space-y-3">
                  {saveError && (
                    <div className="flex items-center space-x-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                      <IconError className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>{saveError}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Sender ID</label>
                      <input
                        className="input w-full text-sm"
                        value={editForm.sender}
                        onChange={e => setEditForm(f => ({ ...f, sender: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Scheduled date <span className="text-gray-400">(YYYY-MM-DD HH:MM:SS)</span>
                      </label>
                      <input
                        className="input w-full text-sm font-mono"
                        placeholder="2025-06-01 09:00:00"
                        value={editForm.scheduled_date}
                        onChange={e => setEditForm(f => ({ ...f, scheduled_date: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                    <textarea
                      className="input w-full text-sm resize-none"
                      rows={3}
                      value={editForm.message}
                      onChange={e => setEditForm(f => ({ ...f, message: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="btn btn-primary flex items-center space-x-1.5 disabled:opacity-60"
                    >
                      {saving && <IconSpinner className="h-3.5 w-3.5 animate-spin" />}
                      <span>{saving ? 'Saving…' : 'Save changes'}</span>
                    </button>
                    <button onClick={cancelEdit} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1.5 flex-wrap gap-y-1">
                      <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {msg.sender || 'Unknown sender'}
                      </span>
                      <span className="flex items-center space-x-1 text-xs text-gray-400">
                        <IconPending className="h-3 w-3" />
                        <span>{msg.scheduled_date || '—'}</span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2">{msg.message || '(no message)'}</p>
                    {(msg.recipient || msg.recipients) && (
                      <p className="text-xs text-gray-400 mt-1">
                        To: {Array.isArray(msg.recipients)
                          ? msg.recipients.slice(0, 5).join(', ') + (msg.recipients.length > 5 ? ` +${msg.recipients.length - 5} more` : '')
                          : (msg.recipient || '')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => startEdit(msg)}
                    className="ml-3 flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary-600 transition-colors"
                    title="Edit scheduled message"
                  >
                    <IconEdit className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ScheduledCampaigns() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [programs, setPrograms]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [toast, setToast]         = useState(null);
  // 'list' | 'calendar'
  const [view, setView]           = useState('list');
  // When switching to calendar after creation, jump to the program's start month
  const [calendarInitMonth, setCalendarInitMonth] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scheduleAPI.list(userId);
      setPrograms(data.programs || []);
    } catch {
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handlePause = async (id) => {
    try { await scheduleAPI.pause(id); showToast('Program paused'); load(); }
    catch { showToast('Failed to pause', 'error'); }
  };

  const handleResume = async (id) => {
    try { await scheduleAPI.resume(id); showToast('Program resumed'); load(); }
    catch { showToast('Failed to resume', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this program? It will stop firing.')) return;
    try { await scheduleAPI.cancel(id); showToast('Program cancelled'); load(); }
    catch { showToast('Failed to cancel', 'error'); }
  };

  const handleRunNow = async (id) => {
    try { await scheduleAPI.runNow(id); showToast('Run triggered — campaign is being sent'); }
    catch { showToast('Failed to trigger run', 'error'); }
  };

  // Called when CreateModal successfully submits
  const handleCreated = (startDate, campaignType) => {
    setShowForm(false);
    // "Program created!" alone let people believe they were finished. Say
    // what is still required of them, and where.
    // Nothing is in the queue yet -- a scheduled campaign generates its
    // content on its first run -- so this says when to expect it rather
    // than sending anyone to an empty page.
    showToast(
      requiresApproval(campaignType)
        ? 'Program created. When it first runs, its messages will wait for you in the Review Queue.'
        : 'Program created',
    );
    load();
    // Jump straight to the calendar, opening on the program's start month
    setCalendarInitMonth(startDate || new Date());
    setView('calendar');
  };

  const activePrograms = programs.filter(p => p.status === 'active');
  const otherPrograms  = programs.filter(p => p.status !== 'active');

  return (
    <div className="max-w-6xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center space-x-2',
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white',
        )}>
          {toast.type === 'error' ? <IconFailed className="h-4 w-4" /> : <IconSuccess className="h-4 w-4" />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduled Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure recurring programs — view your schedule on the calendar and edit before each run.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {/* View toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('list')}
              className={cn(
                'flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <IconList className="h-3.5 w-3.5" />
              <span>List</span>
            </button>
            <button
              onClick={() => setView('calendar')}
              className={cn(
                'flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                view === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <IconCalendar className="h-3.5 w-3.5" />
              <span>Calendar</span>
            </button>
          </div>

          <button onClick={load} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors" title="Refresh">
            <IconRefresh className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
          <button onClick={() => setShowForm(true)} className="btn btn-primary flex items-center space-x-2">
            <IconAdd className="h-4 w-4" />
            <span>New program</span>
          </button>
        </div>
      </div>

      {/* ── CALENDAR VIEW ── */}
      {view === 'calendar' && (
        loading ? (
          <div className="flex items-center justify-center py-24">
            <IconSpinner className="h-7 w-7 animate-spin text-primary-500" />
          </div>
        ) : (
          <CampaignCalendar
            programs={programs}
            onPause={handlePause}
            onResume={handleResume}
            onDelete={handleDelete}
            onRunNow={handleRunNow}
            onRefresh={load}
            initialMonth={calendarInitMonth}
          />
        )
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        loading ? (
          <div className="flex items-center justify-center py-24">
            <IconSpinner className="h-7 w-7 animate-spin text-primary-500" />
          </div>
        ) : programs.length === 0 ? (
          <EmptyState onNew={() => setShowForm(true)} />
        ) : (
          // Full width, matching the header above it. The list used to sit in a
          // max-w-3xl column inside a max-w-6xl page, which left a third of the
          // row empty and put the cards out of line with the title.
          <div className="space-y-6">
            {activePrograms.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Active ({activePrograms.length})
                </h2>
                <ProgramListHeader />
                <div className="space-y-3">
                  {activePrograms.map(p => (
                    <ProgramCard
                      key={p.id}
                      program={p}
                      onPause={handlePause}
                      onResume={handleResume}
                      onDelete={handleDelete}
                      onRunNow={handleRunNow}
                    />
                  ))}
                </div>
              </section>
            )}
            {otherPrograms.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Past & paused ({otherPrograms.length})
                </h2>
                <ProgramListHeader />
                <div className="space-y-3 opacity-80">
                  {otherPrograms.map(p => (
                    <ProgramCard
                      key={p.id}
                      program={p}
                      onPause={handlePause}
                      onResume={handleResume}
                      onDelete={handleDelete}
                      onRunNow={handleRunNow}
                    />
                  ))}
                </div>
              </section>
            )}
            {/* mNotify scheduled queue */}
            <MNotifyQueuePanel />
          </div>
        )
      )}

      {/* Create modal */}
      {showForm && (
        <CreateModal
          userId={userId}
          programs={programs}
          onClose={() => setShowForm(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
